import React, { useState, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { RefreshCw, TrendingUp, DollarSign, FileText, Users } from 'lucide-react';
import { adminReports } from '../../services/api';
import CommissionReport from './reports/CommissionReport';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const formatCurrency = (value) => {
  if (value == null || value === '') return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (d) => {
  if (!d) return '—';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

const defaultDateFrom = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

const defaultDateTo = () => new Date().toISOString().slice(0, 10);

export default function ReportsDashboard() {
  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  // KPI state
  const [summary, setSummary] = useState(null);

  // Revenue chart state
  const [revenueData, setRevenueData] = useState([]);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Other data state
  const [topProducts, setTopProducts] = useState([]);
  const [agingData, setAgingData] = useState(null);
  const [salesByRep, setSalesByRep] = useState([]);

  // Aging tab state
  const [agingTab, setAgingTab] = useState('current');

  // Rep scoping state
  const [isFiltered, setIsFiltered] = useState(false);
  const [repFilterName, setRepFilterName] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Initial parallel load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [summaryRes, revenueRes, topProductsRes, agingRes, salesByRepRes] = await Promise.all([
          adminReports.summary(),
          adminReports.revenue({ dateFrom, dateTo }),
          adminReports.topProducts({ dateFrom, dateTo, limit: 10 }),
          adminReports.aging(),
          adminReports.salesByRep(),
        ]);
        setSummary(summaryRes.data?.data || null);
        setRevenueData(revenueRes.data?.data || []);
        setTopProducts(topProductsRes.data?.data || []);
        setAgingData(agingRes.data?.data || null);
        setSalesByRep(salesByRepRes.data?.data || []);
        if (summaryRes.data?.isFiltered) {
          setIsFiltered(true);
          setRepFilterName(summaryRes.data.repName || '');
        }
      } catch (err) {
        setError('Failed to load report data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revenue re-fetch on date update
  const handleRevenueUpdate = useCallback(async () => {
    setRevenueLoading(true);
    setError('');
    try {
      const [revenueRes, topProductsRes] = await Promise.all([
        adminReports.revenue({ dateFrom, dateTo }),
        adminReports.topProducts({ dateFrom, dateTo, limit: 10 }),
      ]);
      setRevenueData(revenueRes.data?.data || []);
      setTopProducts(topProductsRes.data?.data || []);
    } catch (err) {
      setError('Failed to refresh revenue data. Please try again.');
    } finally {
      setRevenueLoading(false);
    }
  }, [dateFrom, dateTo]);

  // Chart config
  const chartData = {
    labels: revenueData.map((r) => r.month),
    datasets: [
      {
        label: 'Revenue',
        data: revenueData.map((r) => parseFloat(r.revenue)),
        borderColor: 'rgb(13, 110, 253)',
        backgroundColor: 'rgba(13, 110, 253, 0.1)',
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        ticks: {
          callback: (v) => '$' + v.toLocaleString(),
        },
      },
    },
  };

  // Aging helpers
  const agingBuckets = agingData?.buckets || {};
  const agingInvoices = agingData?.invoices || [];

  const bucketDefs = [
    { key: 'current',  label: 'Current',   tabKey: 'current' },
    { key: '1_30',     label: '1–30 Days',  tabKey: '1_30' },
    { key: '31_60',    label: '31–60 Days', tabKey: '31_60' },
    { key: '61_90',    label: '61–90 Days', tabKey: '61_90' },
    { key: '90_plus',  label: '90+ Days',   tabKey: '90_plus' },
  ];

  const overdueInvoices = agingInvoices.filter((inv) => (inv.daysOverdue || 0) > 0);

  const filteredInvoices = agingTab === 'current'
    ? agingInvoices.filter((inv) => (inv.daysOverdue || 0) === 0)
    : overdueInvoices.filter((inv) => {
        const d = inv.daysOverdue || 0;
        if (agingTab === '1_30')   return d >= 1  && d <= 30;
        if (agingTab === '31_60')  return d >= 31 && d <= 60;
        if (agingTab === '61_90')  return d >= 61 && d <= 90;
        if (agingTab === '90_plus') return d > 90;
        return false;
      });

  // Full-page loading spinner
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">

      {/* Rep scoping banner */}
      {isFiltered && (
        <div className="alert alert-info d-flex align-items-center gap-2 py-2 mb-4">
          <span className="badge bg-info text-dark me-1">Your Accounts</span>
          Your dashboard shows data for your assigned accounts only
          {repFilterName && <> — <strong>{repFilterName}</strong></>}.
        </div>
      )}

      {/* Error alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close" />
        </div>
      )}

      {/* Page header */}
      <div className="d-flex align-items-center mb-4">
        <TrendingUp size={24} className="me-2 text-primary" />
        <h4 className="fw-bold mb-0">Reports Dashboard</h4>
      </div>

      {/* ── Tab navigation ── */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link d-flex align-items-center gap-1 ${activeTab === 'commissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('commissions')}
          >
            <DollarSign size={14} />
            Commissions
          </button>
        </li>
      </ul>

      {/* ── Commissions tab ── */}
      {activeTab === 'commissions' && <CommissionReport />}

      {/* ── Overview tab ── */}
      {activeTab === 'overview' && <>

      {/* ── KPI Cards ── */}
      <div className="row g-3 mb-4">
        {/* MTD Revenue */}
        <div className="col-md-3">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body">
              <p className="text-muted small mb-1">MTD Revenue</p>
              <h3 className="fw-bold mb-0">
                {formatCurrency(summary?.mtdRevenue)}
              </h3>
            </div>
          </div>
        </div>

        {/* YTD Revenue */}
        <div className="col-md-3">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body">
              <p className="text-muted small mb-1">YTD Revenue</p>
              <h3 className="fw-bold mb-0">
                {formatCurrency(summary?.ytdRevenue)}
              </h3>
            </div>
          </div>
        </div>

        {/* Open Invoices Total */}
        <div className="col-md-3">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body">
              <p className="text-muted small mb-1">Open Invoices Total</p>
              <h3 className="fw-bold mb-0">
                {formatCurrency(summary?.openInvoicesTotal)}
              </h3>
            </div>
          </div>
        </div>

        {/* Active Customers */}
        <div className="col-md-3">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body">
              <p className="text-muted small mb-1">Active Customers</p>
              <h3 className="fw-bold mb-0">
                {summary?.activeCustomers != null
                  ? Number(summary.activeCustomers).toLocaleString()
                  : '—'}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* ── Revenue Chart ── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <h5 className="fw-semibold mb-0 me-auto">Revenue Over Time</h5>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <label className="form-label mb-0 text-muted small">From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 'auto' }}
                value={dateFrom}
                max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <label className="form-label mb-0 text-muted small">To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 'auto' }}
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
              />
              <button
                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={handleRevenueUpdate}
                disabled={revenueLoading}
              >
                {revenueLoading
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : <RefreshCw size={14} />}
                Update
              </button>
            </div>
          </div>

          {revenueData.length === 0 ? (
            <p className="text-muted text-center py-4">No revenue data for the selected date range.</p>
          ) : (
            <Line data={chartData} options={chartOptions} />
          )}
        </div>
      </div>

      {/* ── Top Products & Outstanding Invoices ── */}
      <div className="row g-4 mb-4">

        {/* Top Products */}
        <div className="col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="fw-semibold mb-3">Top Products</h5>
              {topProducts.length === 0 ? (
                <p className="text-muted">No product data available.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="text-muted small fw-semibold" style={{ width: '40px' }}>#</th>
                        <th className="text-muted small fw-semibold">Product</th>
                        <th className="text-muted small fw-semibold">Vintage</th>
                        <th className="text-muted small fw-semibold">Varietal</th>
                        <th className="text-muted small fw-semibold text-end">Cases</th>
                        <th className="text-muted small fw-semibold text-end">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((p, i) => (
                        <tr key={p.id || i}>
                          <td className="text-muted small">{i + 1}</td>
                          <td className="fw-medium small">{p.name || p.product || '—'}</td>
                          <td className="small">{p.vintage || '—'}</td>
                          <td className="small">{p.varietal || '—'}</td>
                          <td className="small text-end">{p.cases != null ? Number(p.cases).toLocaleString() : '—'}</td>
                          <td className="small text-end">{formatCurrency(p.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Outstanding Invoices (Aging) */}
        <div className="col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="fw-semibold mb-3">Outstanding Invoices — Aging</h5>

              {/* Aging bucket stat cards */}
              <div className="row g-2 mb-3">
                {bucketDefs.map(({ key, label, tabKey }) => {
                  const bucket = agingBuckets[key] || { count: 0, total: 0 };
                  const isActive = agingTab === tabKey;
                  return (
                    <div className="col" key={key}>
                      <div
                        className={`card border text-center py-2 px-1 cursor-pointer ${isActive ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setAgingTab(tabKey)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setAgingTab(tabKey)}
                      >
                        <p className="text-muted mb-0" style={{ fontSize: '0.7rem' }}>{label}</p>
                        <p className={`fw-bold mb-0 ${isActive ? 'text-primary' : ''}`} style={{ fontSize: '0.85rem' }}>
                          {bucket.count || 0}
                        </p>
                        <p className={`mb-0 ${isActive ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                          {formatCurrency(bucket.total)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Invoices table for selected tab */}
              {filteredInvoices.length === 0 ? (
                <p className="text-muted small text-center py-2">No invoices in this bucket.</p>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th className="text-muted small fw-semibold">Invoice #</th>
                        <th className="text-muted small fw-semibold">Customer</th>
                        <th className="text-muted small fw-semibold">Due Date</th>
                        <th className="text-muted small fw-semibold text-end">Days</th>
                        <th className="text-muted small fw-semibold text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((inv, i) => (
                        <tr key={inv.id || i}>
                          <td className="small fw-medium">{inv.invoiceNumber || inv.invoice_number || inv.id || '—'}</td>
                          <td className="small">{inv.customerName || inv.customer_name || '—'}</td>
                          <td className="small">{formatDate(inv.dueDate || inv.due_date)}</td>
                          <td className="small text-end">
                            {inv.daysOverdue != null
                              ? <span className={inv.daysOverdue > 0 ? 'text-danger fw-semibold' : 'text-success'}>
                                  {inv.daysOverdue > 0 ? `+${inv.daysOverdue}` : '0'}
                                </span>
                              : '—'}
                          </td>
                          <td className="small text-end">{formatCurrency(inv.amount || inv.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sales by Rep ── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <h5 className="fw-semibold mb-3">Sales by Rep</h5>
          {salesByRep.length === 0 ? (
            <p className="text-muted">No sales rep data available.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-muted small fw-semibold">Rep Name</th>
                    <th className="text-muted small fw-semibold text-end">Customers</th>
                    <th className="text-muted small fw-semibold text-end">MTD Orders</th>
                    <th className="text-muted small fw-semibold text-end">MTD Revenue</th>
                    <th className="text-muted small fw-semibold text-end">YTD Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {salesByRep.map((rep, i) => (
                    <tr key={rep.id || i}>
                      <td className="fw-medium">
                        {rep.repName || rep.rep_name || rep.name || '—'}
                      </td>
                      <td className="text-end">
                        {rep.customers != null ? Number(rep.customers).toLocaleString() : '—'}
                      </td>
                      <td className="text-end">
                        {rep.mtdOrders != null ? Number(rep.mtdOrders).toLocaleString() : '—'}
                      </td>
                      <td className="text-end">
                        {formatCurrency(rep.mtdRevenue != null ? rep.mtdRevenue : rep.mtd_revenue)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(rep.ytdRevenue != null ? rep.ytdRevenue : rep.ytd_revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <td className="fw-semibold">Totals</td>
                    <td className="text-end fw-semibold">
                      {salesByRep.reduce((s, r) => s + (Number(r.customers) || 0), 0).toLocaleString()}
                    </td>
                    <td className="text-end fw-semibold">
                      {salesByRep.reduce((s, r) => s + (Number(r.mtdOrders) || 0), 0).toLocaleString()}
                    </td>
                    <td className="text-end fw-semibold">
                      {formatCurrency(
                        salesByRep.reduce((s, r) => s + parseFloat(r.mtdRevenue ?? r.mtd_revenue ?? 0), 0)
                      )}
                    </td>
                    <td className="text-end fw-semibold">
                      {formatCurrency(
                        salesByRep.reduce((s, r) => s + parseFloat(r.ytdRevenue ?? r.ytd_revenue ?? 0), 0)
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      </> /* end overview tab */}

    </div>
  );
}
